const baseUrl = process.env.API_URL + "/UserManagementApi";
 
 // Set to the local Function App URL to test against a locally running backend
 // (e.g. "http://localhost:7071/api"). Leave "" to use the production gateway.
 var LOCAL_COPILOT_URL = "";
 
 function getBase() {
   return LOCAL_COPILOT_URL || baseUrl;
 }
 
 // NOTE: window.httpService.post(url, body) does NOT serialize a JSON body — it
 // treats the 2nd arg as request options, so the payload leaks into headers and the
 // body goes out empty (Content-Length: 0), which the backend rejects with 400.
 // httpService only reliably handles GET. So we send POST bodies with fetch (which
 // guarantees a real JSON body) and reuse httpService just to obtain the auth token.
 
 // Cached bearer token harvested from httpService (refreshed on demand / on 401).
 var cachedAuthHeader = null;
 
 // Harvest the AAD bearer token the app already uses. httpService attaches the
 // Authorization header to outgoing GETs, so we read it back from the response config.
 function harvestAuthHeader() {
   return window.httpService
     .get(baseUrl + "/GetFiscalYears")
     .then(function (resp) {
       var config = (resp && resp.config) || {};
       var h = config.headers || {};
       var auth = h.Authorization || h.authorization;
       if (auth) cachedAuthHeader = auth;
       return cachedAuthHeader;
     })
     .catch(function () {
       return cachedAuthHeader;
     });
 }
 
 // Build request headers with the bearer token + fiscalYear/planningCycle context.
 // Auth and context headers are always sent — the copilot backend forwards them to
 // the CR API which requires AAD auth at the gateway level, even when the local
 // Function App itself is anonymous.
 function buildHeaders() {
   var headers = { "Content-Type": "application/json" };
 
   try {
     if (window.httpService && window.httpService.fiscalYear != null) {
       headers["fiscalyear"] = String(window.httpService.fiscalYear);
     }
     if (window.httpService && window.httpService.planningCycle != null) {
       headers["planningcycle"] = String(window.httpService.planningCycle);
     }
   } catch (e) {
     /* ignore */
   }
 
   var tokenPromise = cachedAuthHeader
     ? Promise.resolve(cachedAuthHeader)
     : harvestAuthHeader();
   return tokenPromise.then(function (auth) {
     if (auth) headers["Authorization"] = auth;
     return headers;
   });
 }
 
 // Custom error class for API errors with status code.
 class CopilotApiError extends Error {
   constructor(status, message, detail) {
     super(message);
     this.name = "CopilotApiError";
     this.status = status;
     this.detail = detail;
   }
 }
 
 // POST JSON helper — sends the payload as a real JSON body (Content-Length != 0).
 // `signal` (optional) wires an AbortController for cancellation.
 function postJson(url, headers, bodyObj, signal) {
   return fetch(url, {
     method: "POST",
     headers: headers,
     body: JSON.stringify(bodyObj),
     signal: signal,
   }).then(function (res) {
     if (res.status === 204) {
       return null; // server cancelled / no content
     }
     if (res.status === 401) {
       // Token likely expired — drop the cache so the next call re-harvests it.
       cachedAuthHeader = null;
     }
     if (!res.ok) {
       return res.text().then(function (t) {
         let detail = t;
         try {
           const json = JSON.parse(t);
           detail = json.error || json.detail || t;
         } catch (e) {
           /* not JSON — use raw text */
         }
         throw new CopilotApiError(res.status, "Copilot API error", detail);
       });
     }
     return res.json();
   });
 }
 
 // 1. Create a conversation -> returns conversationId (string).
 // userId should be the logged-in user's alias/MS account (used server-side to scope history).
 export function createConversation(title, userId) {
   return buildHeaders().then(function (headers) {
     return postJson(getBase() + "/copilot/conversations", headers, {
       title: title || "Mint Studio Chat",
       userId: userId,
     }).then(function (r) {
       return r.conversationId;
     });
   });
 }
 
 // 2. Post a turn -> returns { conversationId, messageId, reply, citations, finishReason }.
 // messageId MUST be unique per message (idempotency key).
 // signal (optional) lets the caller abort the request.
 // userId should be the logged-in user's alias/MS account.
 export function postTurn(conversationId, userText, messageId, signal, userId) {
   return buildHeaders().then(function (headers) {
     return postJson(
       getBase() + "/copilot/turn",
       headers,
       {
         conversationId: conversationId,
         messageId: messageId,
         userText: userText,
         userId: userId,
       },
       signal
     );
   });
 }
 
 // 3. Server-side cancel by operationId. NOTE: PostTurn returns the operationId only
 // after completion, so this is rarely usable from the UI; prefer AbortController.
 export function cancelTurn(operationId) {
   return buildHeaders().then(function (headers) {
     return postJson(getBase() + "/copilot/cancel", headers, {
       operationId: operationId,
     });
   });
 }
 
 // 4. Check if the user has access to Copilot chat feature.
 // Returns { userId: string, allowed: boolean }
 export function checkCopilotAccess(userId) {
   if (!userId) {
     return Promise.resolve({ userId: userId, allowed: false });
   }
 
   return buildHeaders()
     .then(function (headers) {
       return postJson(getBase() + "/copilot/access", headers, { userId: userId });
     })
     .catch(function (err) {
       console.warn("Copilot access check failed:", err);
       return { userId: userId, allowed: false };
     });
 }
